import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Building2, Wand2, Rss, Sparkles, Loader2, Globe, Images, MessageCircle, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandVoiceDialog from "@/components/BrandVoiceDialog";
import RssFeedsDialog from "@/components/RssFeedsDialog";
import AutoReplyDialog from "@/components/AutoReplyDialog";
import BusinessUnitsDialog from "@/components/BusinessUnitsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AI_FIELDS = new Set([
  "industry", "services", "tonality", "target_audience", "image_style",
  "forbidden_statements", "hashtag_rules", "tagline", "brand_color",
  "cta_templates", "posts_per_week", "reels_per_week",
]);

const emptyForm = {
  name: "", industry: "", services: "", tonality: "", target_audience: "",
  platforms: ["instagram", "facebook"], image_style: "", forbidden_statements: "",
  hashtag_rules: "", cta_templates: [], posts_per_week: 3, reels_per_week: 1,
  stories_per_week: 0,
  preferred_days: [], preferred_times: [], instagram_account: "", facebook_page: "",
  website: "", emoji_style: "",
  geo_mode: "", geo_region: "", geo_radius_km: 20, ai_rules_must: "", ai_rules_forbidden: "", ai_brand_brief: "",
  logo_url: "", brand_color: "", tagline: "",
  auto_post_media_pool: "all",  // "all" | "uploaded"
  auto_fill_week_enabled: false,
  auto_fill_weekday: 6,  // 0=Mon ... 6=Sun
  auto_fill_time: "19:00",
  auto_approve: false,
  ai_video_enabled: false,
};

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [rulesRaw, setRulesRaw] = useState("");
  const [rulesBusy, setRulesBusy] = useState(false);

  const structureRules = async () => {
    if (!rulesRaw.trim()) { toast.error("Bitte zuerst formlos beschreiben, was die KI beachten soll"); return; }
    setRulesBusy(true);
    try {
      const r = await axios.post(`${API}/companies/${editing || "neu"}/ai/structure-rules`,
        { raw_text: rulesRaw }, { withCredentials: true });
      setForm((f) => ({ ...f, ai_rules_must: r.data.must, ai_rules_forbidden: r.data.forbidden }));
      setRulesRaw("");
      toast.success("Regeln strukturiert & korrigiert — bitte prüfen und speichern");
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Strukturierung fehlgeschlagen"); }
    finally { setRulesBusy(false); }
  };
  const [ctaInput, setCtaInput] = useState("");
  const [zfxQ, setZfxQ] = useState("");
  const [zfxHits, setZfxHits] = useState([]);
  const [zfxBusy, setZfxBusy] = useState(false);

  const zefixSearch = async () => {
    if (zfxQ.trim().length < 3) { toast.error("Mindestens 3 Zeichen"); return; }
    setZfxBusy(true);
    try {
      const r = await axios.get(`${API}/zefix/search`, { params: { q: zfxQ.trim(), limit: 6 }, withCredentials: true });
      setZfxHits(r.data.results.filter((h) => h.active));
    } catch (e) { toast.error("Zefix-Suche fehlgeschlagen"); }
    setZfxBusy(false);
  };
  const [voiceFor, setVoiceFor] = useState(null);
  const [rssFor, setRssFor] = useState(null);
  const [autoPostFor, setAutoPostFor] = useState(null);
  const [autoReplyFor, setAutoReplyFor] = useState(null);
  const [unitsFor, setUnitsFor] = useState(null);
  // AI Auto-Fill state
  const [aiUrl, setAiUrl] = useState("");
  const [aiBrief, setAiBrief] = useState("");
  const [aiBusyAll, setAiBusyAll] = useState(false);
  const [aiBusyField, setAiBusyField] = useState(null); // field key currently loading
  const { t } = useLanguage();

  const load = async () => {
    try {
      const res = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompanies(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/companies/${editing}`, form, { withCredentials: true });
        toast.success("Firma aktualisiert");
      } else {
        await axios.post(`${API}/companies`, form, { withCredentials: true });
        toast.success("Firma erstellt");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (c) => {
    setEditing(c.id);
    setForm({ ...emptyForm, ...c });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Firma wirklich loeschen?")) return;
    try {
      await axios.delete(`${API}/companies/${id}`, { withCredentials: true });
      toast.success("Firma geloescht");
      load();
    } catch { toast.error("Fehler beim Loeschen"); }
  };

  const addCta = () => {
    if (ctaInput.trim()) {
      setForm((f) => ({ ...f, cta_templates: [...(f.cta_templates || []), ctaInput.trim()] }));
      setCtaInput("");
    }
  };

  const removeCta = (i) =>
    setForm((f) => ({ ...f, cta_templates: f.cta_templates.filter((_, idx) => idx !== i) }));

  // ---------------- AI Auto-Fill ----------------
  const runAi = async (fields) => {
    if (!form.name?.trim()) {
      toast.error("Bitte zuerst Firmenname eingeben");
      return;
    }
    if (!aiUrl.trim() && !aiBrief.trim()) {
      toast.error("Bitte URL oder Brief eingeben");
      return;
    }
    const isAll = !fields || fields.length > 1;
    if (isAll) setAiBusyAll(true); else setAiBusyField(fields[0]);
    try {
      const res = await axios.post(
        `${API}/ai/company-research`,
        {
          company_name: form.name.trim(),
          url: aiUrl.trim() || undefined,
          brief: aiBrief.trim() || undefined,
          fields: fields || undefined,
          existing: form,
        },
        { withCredentials: true },
      );
      const got = res.data?.fields || {};
      const keys = Object.keys(got);
      if (keys.length === 0) {
        toast.warning("Keine Daten erkannt — Brief verfeinern oder andere URL versuchen");
        return;
      }
      setForm((f) => ({ ...f, ...got, ...(aiUrl.trim() ? { website: aiUrl.trim() } : {}) }));
      toast.success(`KI hat ${keys.length} Feld(er) ausgefuellt`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KI-Auto-Fill fehlgeschlagen");
    } finally {
      setAiBusyAll(false);
      setAiBusyField(null);
    }
  };

  const AiBtn = ({ fieldKey }) => (
    <button
      type="button"
      onClick={() => runAi([fieldKey])}
      disabled={aiBusyField === fieldKey || aiBusyAll}
      className="ml-1 inline-flex items-center gap-1 text-[10px] text-[#2563EB] hover:text-[#1D4ED8] disabled:opacity-50"
      title="Mit KI ausfuellen"
      data-testid={`ai-fill-${fieldKey}`}
    >
      {aiBusyField === fieldKey ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Sparkles className="w-3 h-3" />
      )}
      KI
    </button>
  );

  const field = (key, label, type = "input") => (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium text-[var(--studio-text-muted)]">{label}</Label>
        {AI_FIELDS.has(key) && <AiBtn fieldKey={key} />}
      </div>
      {type === "textarea" ? (
        <Textarea
          value={form[key] || ""}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className="mt-1"
          rows={2}
          data-testid={`field-${key}`}
        />
      ) : type === "number" ? (
        <Input
          type="number"
          value={form[key] ?? 0}
          onChange={(e) => setForm((f) => ({ ...f, [key]: parseInt(e.target.value) || 0 }))}
          className="mt-1"
          data-testid={`field-${key}`}
        />
      ) : (
        <Input
          value={form[key] || ""}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className="mt-1"
          data-testid={`field-${key}`}
        />
      )}
    </div>
  );

  return (
    <div data-testid="companies-page" className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">Firmenverwaltung</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">
            Verwalte deine Firmen und deren Markenprofile
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); setAiUrl(""); setAiBrief(""); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              <Plus className="w-4 h-4 mr-2" /> {t("companies.add_btn")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editing ? t("companies.edit_title") : t("companies.new_title")}
              </DialogTitle>
            </DialogHeader>

            {/* Zefix Handelsregister-Suche */}
            <div className="mt-4 p-3 rounded-lg border border-emerald-500/30 bg-emerald-50/40 space-y-2" data-testid="zefix-company-panel">
              <div className="flex items-center gap-2 text-xs font-medium text-emerald-700">
                Handelsregister-Suche (Zefix) — offiziellen Firmennamen übernehmen
              </div>
              <div className="flex gap-2">
                <Input value={zfxQ} onChange={(e) => setZfxQ(e.target.value)} placeholder="Firmenname im Handelsregister suchen…"
                  className="text-sm h-9" data-testid="zefix-company-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); zefixSearch(); } }} />
                <Button type="button" onClick={zefixSearch} disabled={zfxBusy} className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="zefix-company-search-btn">
                  {zfxBusy ? "Suche…" : "Suchen"}
                </Button>
              </div>
              {zfxHits.length > 0 && (
                <div className="space-y-1" data-testid="zefix-company-hits">
                  {zfxHits.map((h) => (
                    <button key={h.ehraid} type="button"
                      className="w-full text-left text-xs px-2.5 py-1.5 bg-white border border-emerald-200 rounded hover:bg-emerald-50"
                      onClick={() => { setForm((f) => ({ ...f, name: h.name })); setZfxHits([]); toast.success(`Übernommen: ${h.name} (${h.uid}, ${h.seat})`); }}>
                      <b>{h.name}</b> — {h.seat} · {h.legal_form} · {h.uid}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* KI-Auto-Fill Panel */}
            <div
              className="mt-4 p-3 rounded-lg border border-[#2563EB]/30 bg-gradient-to-br from-[#2563EB]/5 to-transparent space-y-2"
              data-testid="ai-autofill-panel"
            >
              <div className="flex items-center gap-2 text-xs font-medium text-[#2563EB]">
                <Sparkles className="w-3.5 h-3.5" />
                KI-Auto-Fill — analysiert die Firma und füllt das Formular
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <div className="relative">
                  <Globe className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--studio-text-muted)]" />
                  <Input
                    value={aiUrl}
                    onChange={(e) => setAiUrl(e.target.value)}
                    placeholder="https://www.firmen-website.ch"
                    className="pl-7 text-sm h-9"
                    data-testid="ai-url-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => runAi(null)}
                  disabled={aiBusyAll || !form.name?.trim() || (!aiUrl.trim() && !aiBrief.trim())}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-9 text-xs"
                  data-testid="ai-fill-all-btn"
                  title={
                    !form.name?.trim()
                      ? "Bitte zuerst Firmennamen eingeben"
                      : (!aiUrl.trim() && !aiBrief.trim())
                      ? "Bitte URL oder Brief eingeben"
                      : "KI fuellt alle Felder aus"
                  }
                >
                  {aiBusyAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Alles ausfüllen
                </Button>
              </div>
              <Textarea
                value={aiBrief}
                onChange={(e) => setAiBrief(e.target.value)}
                placeholder="Optional: zusätzlicher Brief / Notizen zur Firma (z.B. 'Nischen-Personal-Trainer für Frauen 35+, Ziel Kraftaufbau')"
                rows={2}
                className="text-xs"
                data-testid="ai-brief-input"
              />
              {!form.name?.trim() ? (
                <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠ Bitte zuerst <b>Firmennamen</b> unten eingeben — danach KI starten.
                </p>
              ) : (
                <p className="text-[10px] text-[var(--studio-text-muted)]">
                  Tipp: KI nutzt OpenAI GPT-5.2 mit URL+Brief. ✨ neben jedem Feld füllt nur dieses einzeln. Bilder/Logos werden NICHT von der KI verwendet — nur Texte und Hex-Farbe.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {field("name", t("companies.name") + " *")}
              {field("industry", t("companies.industry"))}
              {field("services", t("companies.services"), "textarea")}
              {field("tonality", t("companies.tonality"))}
              {field("target_audience", t("companies.target_audience"))}
              {field("image_style", t("companies.image_style"))}
              {field("forbidden_statements", t("companies.forbidden"), "textarea")}
              {field("hashtag_rules", t("companies.hashtag_rules"))}
              {field("website", "Webseite (wird als CTA in Posts verwendet)")}
              {field("emoji_style", "Emoji-Set der Marke (z. B. 🇮🇹 🤍❤️ ☀️ 🍝 🌿 — wird wiedererkennbar in Posts verwendet)")}
              <div className="col-span-2 border border-[var(--studio-border)] rounded-lg p-3 space-y-2" data-testid="geo-settings">
                <Label className="text-xs font-medium text-[var(--studio-text-muted)]">
                  Reichweite / Region — steuert, ob die KI lokale oder schweizweite Hashtags & Ortsbezug in Posts einbaut
                </Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: "", l: "Keine Vorgabe" },
                    { v: "regional", l: "📍 Regional (mit Umkreis)" },
                    { v: "national", l: "🇨🇭 Schweizweit" },
                  ].map((o) => (
                    <Button key={o.v} type="button" size="sm"
                      variant={(form.geo_mode || "") === o.v ? "default" : "outline"}
                      className="h-8 text-xs"
                      onClick={() => setForm((f) => ({ ...f, geo_mode: o.v }))}
                      data-testid={`geo-mode-${o.v || "none"}`}>
                      {o.l}
                    </Button>
                  ))}
                </div>
                {form.geo_mode === "regional" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-[var(--studio-text-muted)]">Region / Ort *</Label>
                      <Input className="mt-1" value={form.geo_region || ""} placeholder="z. B. Rapperswil-Jona SG"
                        onChange={(e) => setForm((f) => ({ ...f, geo_region: e.target.value }))}
                        data-testid="geo-region-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-[var(--studio-text-muted)]">Umkreis (km)</Label>
                      <Input className="mt-1" type="number" min="5" max="150" value={form.geo_radius_km ?? 20}
                        onChange={(e) => setForm((f) => ({ ...f, geo_radius_km: parseInt(e.target.value) || 20 }))}
                        data-testid="geo-radius-input" />
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-[var(--studio-text-muted)]">
                  Regional: KI nutzt Orts-Hashtags aus der Region + Umkreis (z. B. #rapperswil #fitnessrapperswil) und erwähnt den Ort im Text.
                  Schweizweit: KI nutzt nationale Hashtags (#schweiz + Branche) und vermeidet enge Orts-Hashtags.
                </p>

                <div className="border-t border-[var(--studio-border)] pt-3 space-y-3">
                  <Label className="text-xs font-semibold">✍️ KI-Schreibregeln (gelten für ALLE KI-Texte dieser Firma)</Label>
                  <div className="bg-[var(--studio-bg)] border border-[var(--studio-border)] rounded-lg p-3 space-y-2">
                    <Label className="text-xs font-medium">Einfach formlos beschreiben — die KI erstellt die Auflistung und korrigiert deine Wörter</Label>
                    <Textarea className="text-sm bg-white" rows={2} value={rulesRaw}
                      placeholder="z. B. beim fiat500 nix mit auf anfrage schreiben, preise stehn auf der webseite, kunde soll direkt nachschauen könen, immer sie-form"
                      onChange={(e) => setRulesRaw(e.target.value)}
                      data-testid="ai-rules-raw-input" />
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs gap-1"
                      onClick={structureRules} disabled={rulesBusy || !editing}
                      title={!editing ? "Zuerst Firma speichern, dann Regeln strukturieren" : undefined}
                      data-testid="ai-rules-structure-btn">
                      {rulesBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "✨"} Mit KI strukturieren &amp; korrigieren
                    </Button>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-blue-700">KI-BRIEFING — komplettes Markenprofil (Konzepte, Angebote, Zielgruppen, Hooks, Plattform-Regeln)</Label>
                    <Textarea className="mt-1 text-sm font-mono" rows={10} value={form.ai_brand_brief || ""}
                      placeholder={"Füge hier das komplette KI-Briefing der Firma ein — z.B.:\n• Über die Firma & Motto\n• Angebote/Produkte im Detail\n• Zielgruppen (B2B/B2C) und wie sie angesprochen werden\n• Beispiel-Hooks und CTAs\n• Content-Mix und Plattform-Regeln (Instagram/TikTok/LinkedIn/Facebook)\n• Was NIE erfunden werden darf (Preise, Standorte, Events ...)"}
                      onChange={(e) => setForm((f) => ({ ...f, ai_brand_brief: e.target.value }))}
                      data-testid="ai-brand-brief-input" />
                    <p className="text-[10px] text-[var(--studio-text-muted)] mt-0.5">
                      {(form.ai_brand_brief || "").length.toLocaleString("de-CH")} Zeichen — wird bei JEDER KI-Generierung (Posts, Reels, Wochenplaner, Autopilot) automatisch mitgegeben.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-emerald-700">MUSS — das soll die KI immer beachten/erwähnen</Label>
                    <Textarea className="mt-1 text-sm" rows={2} value={form.ai_rules_must || ""}
                      placeholder="z. B. Preise sind online unter bellastrada.ch/preise einsehbar — immer darauf verweisen. Persönliche Übergabe in Wetzikon ZH erwähnen."
                      onChange={(e) => setForm((f) => ({ ...f, ai_rules_must: e.target.value }))}
                      data-testid="ai-rules-must-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-red-700">VERBOTEN — das darf die KI nie schreiben</Label>
                    <Textarea className="mt-1 text-sm" rows={2} value={form.ai_rules_forbidden || ""}
                      placeholder="z. B. Nie 'Preis auf Anfrage' oder 'Offerte anfordern' schreiben. Keine Rabattversprechen. Kein Du — immer Sie."
                      onChange={(e) => setForm((f) => ({ ...f, ai_rules_forbidden: e.target.value }))}
                      data-testid="ai-rules-forbidden-input" />
                  </div>
                  <p className="text-[10px] text-[var(--studio-text-muted)]">
                    Diese Regeln werden automatisch in jeden KI-Prompt eingefügt: Posts, Captions, Wochenplaner, Cockpit-Ideen und Anzeigen-Texte.
                  </p>
                </div>
              </div>
              {field("instagram_account", "Instagram Account")}
              {field("facebook_page", "Facebook Page")}
              {field("logo_url", "Logo-URL (Branding)")}
              {field("tagline", "Tagline (Branding)")}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-[var(--studio-text-muted)]">Markenfarbe (Hex)</Label>
                  <AiBtn fieldKey="brand_color" />
                </div>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={form.brand_color || "#2563EB"}
                    onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))}
                    className="w-14 p-1 h-10"
                    data-testid="brand-color-picker"
                  />
                  <Input
                    value={form.brand_color || ""}
                    onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))}
                    placeholder="#2563EB"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* === Content-Plan Section === */}
            <div className="mt-6 p-4 rounded-lg border-2 border-[#2563EB]/30 bg-gradient-to-br from-[#2563EB]/5 to-transparent space-y-4" data-testid="content-plan-section">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-[#2563EB]" />
                <h3 className="text-sm font-semibold">Content-Plan pro Woche</h3>
              </div>
              <p className="text-[11px] text-[var(--studio-text-muted)]">
                Waehle die Plattformen und definiere wie viele Posts, Reels und Stories die KI pro Woche erstellen soll.
              </p>

              {/* Platform Selector */}
              <div>
                <Label className="text-[10px] text-[var(--studio-text-muted)] uppercase">Aktive Plattformen</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="platform-selector">
                  {[
                    { id: "instagram", label: "Instagram", color: "#E1306C" },
                    { id: "facebook",  label: "Facebook",  color: "#1877F2" },
                    { id: "tiktok",    label: "TikTok",    color: "#000000" },
                    { id: "linkedin",  label: "LinkedIn",  color: "#0A66C2" },
                  ].map((p) => {
                    const active = (form.platforms || []).includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setForm((f) => ({
                          ...f,
                          platforms: active
                            ? (f.platforms || []).filter((x) => x !== p.id)
                            : [ ...(f.platforms || []), p.id ],
                        }))}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium border-2 transition-all ${
                          active
                            ? "text-white shadow-sm"
                            : "bg-white text-[var(--studio-text-muted)] border-[var(--studio-border)] hover:border-gray-400"
                        }`}
                        style={active ? { background: p.color, borderColor: p.color } : {}}
                        data-testid={`platform-toggle-${p.id}`}
                      >
                        {p.label} {active && "✓"}
                      </button>
                    );
                  })}
                </div>
                {(!form.platforms || form.platforms.length === 0) && (
                  <p className="text-[10px] text-amber-700 mt-1.5">
                    Mindestens eine Plattform auswaehlen, sonst werden keine Posts veroeffentlicht.
                  </p>
                )}
              </div>

              <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-[11px] text-blue-800">
                Die Wochenplanung (Posts/Reels/Stories pro Woche) wird jetzt in der{" "}
                <a href="/setup" className="underline font-semibold">Setup-Anleitung</a> der Firma gepflegt.
              </div>

              {/* Auto-Approve Toggle */}
              <label className="flex items-start justify-between gap-3 cursor-pointer p-3 rounded-md bg-white/40 border border-[var(--studio-border)]">
                <div className="flex-1">
                  <div className="text-xs font-medium flex items-center gap-1.5">
                    Auto-Freigabe
                    {form.auto_approve && <Badge className="bg-green-600 text-white text-[9px] h-4">AKTIV</Badge>}
                  </div>
                  <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                    Wenn aktiviert, werden alle von der KI erstellten Posts <strong>automatisch freigegeben</strong> (kein manueller Review noetig).
                    {" "}Sie gehen direkt vom Entwurf auf <em>Freigegeben</em> und werden zum geplanten Zeitpunkt veroeffentlicht.
                    {!form.auto_approve && <span className="block mt-1 text-amber-700">Achtung: Ohne diese Option musst du jeden Post in der Freigabe-Seite manuell freigeben.</span>}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!form.auto_approve}
                  onChange={(e) => setForm((f) => ({ ...f, auto_approve: e.target.checked }))}
                  className="w-4 h-4 accent-[#2563EB] mt-0.5"
                  data-testid="auto-approve-toggle"
                />
              </label>

              {/* KI-Video Toggle */}
              <label className="flex items-start justify-between gap-3 cursor-pointer p-3 rounded-md bg-white/40 border border-[var(--studio-border)]">
                <div className="flex-1">
                  <div className="text-xs font-medium flex items-center gap-1.5">
                    KI-Videos (Reels)
                    {form.ai_video_enabled && <Badge className="bg-green-600 text-white text-[9px] h-4">AKTIV</Badge>}
                  </div>
                  <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                    Wenn aktiviert, darf die KI fuer diese Firma <strong>Reel-Videos generieren</strong> (manuell ueber
                    "KI-Reel erstellen" auf der Freigabe-Seite und automatisch beim Auto-Fill). Fotos erstellt die KI unabhaengig von diesem Schalter.
                    <span className="block mt-1 text-amber-700">Hinweis: Video-Generierung dauert 2-5 Minuten und verbraucht deutlich mehr KI-Budget als Bilder.</span>
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!form.ai_video_enabled}
                  onChange={(e) => setForm((f) => ({ ...f, ai_video_enabled: e.target.checked }))}
                  className="w-4 h-4 accent-[#2563EB] mt-0.5"
                  data-testid="ai-video-toggle"
                />
              </label>

              {/* Fotos als Video-Vorlage */}
              {form.ai_video_enabled && (
                <label className="flex items-start justify-between gap-3 cursor-pointer p-3 rounded-md bg-white/40 border border-[var(--studio-border)]">
                  <div className="flex-1">
                    <div className="text-xs font-medium">Fotos als Video-Vorlage verwenden</div>
                    <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                      Wenn aktiviert, darf die KI <strong>hochgeladene Fotos als visuellen Startpunkt</strong> fuer Videos verwenden
                      (das Foto praegt Motiv und Look des Videos). Deaktiviert = Videos entstehen nur aus Text-Beschreibungen.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.ai_video_use_photos !== false}
                    onChange={(e) => setForm((f) => ({ ...f, ai_video_use_photos: e.target.checked }))}
                    className="w-4 h-4 accent-[#2563EB] mt-0.5"
                    data-testid="ai-video-photos-toggle"
                  />
                </label>
              )}
            </div>

            {/* CTA Templates */}
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-[var(--studio-text-muted)]">{t("companies.cta_label")}</Label>
                <AiBtn fieldKey="cta_templates" />
              </div>
              <div className="flex gap-2 mt-1">
                <Input
                  value={ctaInput}
                  onChange={(e) => setCtaInput(e.target.value)}
                  placeholder="z.B. Jetzt buchen"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCta(); } }}
                />
                <Button type="button" variant="outline" onClick={addCta}>+</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.cta_templates?.map((cta, i) => (
                  <Badge key={i} variant="secondary" className="cursor-pointer hover:bg-red-100" onClick={() => removeCta(i)}>
                    {cta} &times;
                  </Badge>
                ))}
              </div>
            </div>

            {/* KI-Auto-Post: Medien-Pool */}
            <div className="mt-4 p-3 rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg)]/40 space-y-2">
              <Label className="text-xs font-medium text-[var(--studio-text-muted)]">
                KI-Auto-Post — welche Medien darf die KI verwenden?
              </Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "all", label: "Alle Medien (Eigene + KI-Bilder)", desc: "Maximale Inhalts-Vielfalt" },
                  { value: "uploaded", label: "Nur eigene Uploads", desc: "KI-generierte Bilder werden ausgeschlossen" },
                ].map((o) => {
                  const active = (form.auto_post_media_pool || "all") === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, auto_post_media_pool: o.value }))}
                      className={`flex-1 min-w-[200px] p-2 rounded-lg border text-left transition-all ${
                        active
                          ? "border-[#2563EB] bg-[#2563EB]/5 ring-1 ring-[#2563EB]/30"
                          : "border-[var(--studio-border)] hover:bg-[var(--studio-bg)]"
                      }`}
                      data-testid={`pool-${o.value}-btn`}
                    >
                      <p className="text-xs font-medium">{o.label}</p>
                      <p className="text-[10px] text-[var(--studio-text-muted)]">{o.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recurring Auto-Fill Schedule */}
            <div className="mt-3 p-3 rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg)]/40 space-y-2">
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <div>
                  <p className="text-xs font-medium">Wochenplaner — automatisch befuellen</p>
                  <p className="text-[10px] text-[var(--studio-text-muted)]">
                    Befuellt jede Woche automatisch die kommende Woche aus den unverwendeten Medien.
                    Bestehende Slots werden nicht ueberschrieben.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!form.auto_fill_week_enabled}
                  onChange={(e) => setForm((f) => ({ ...f, auto_fill_week_enabled: e.target.checked }))}
                  className="w-4 h-4 accent-[#2563EB]"
                  data-testid="auto-fill-toggle"
                />
              </label>
              {form.auto_fill_week_enabled && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <Label className="text-[10px] text-[var(--studio-text-muted)]">Wochentag</Label>
                    <select
                      value={form.auto_fill_weekday ?? 6}
                      onChange={(e) => setForm((f) => ({ ...f, auto_fill_weekday: parseInt(e.target.value) }))}
                      className="mt-1 w-full px-2 py-1 text-xs rounded-md border border-[var(--studio-border)] bg-white"
                      data-testid="auto-fill-weekday"
                    >
                      <option value={0}>Montag</option>
                      <option value={1}>Dienstag</option>
                      <option value={2}>Mittwoch</option>
                      <option value={3}>Donnerstag</option>
                      <option value={4}>Freitag</option>
                      <option value={5}>Samstag</option>
                      <option value={6}>Sonntag</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-[var(--studio-text-muted)]">Uhrzeit (HH:MM)</Label>
                    <Input
                      type="time"
                      value={form.auto_fill_time || "19:00"}
                      onChange={(e) => setForm((f) => ({ ...f, auto_fill_time: e.target.value }))}
                      className="mt-1 h-8 text-xs"
                      data-testid="auto-fill-time"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); setForm(emptyForm); }}>
                {t("companies.cancel")}
              </Button>
              <Button
                onClick={handleSave}
                data-testid="save-company-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                disabled={!form.name}
              >
                {editing ? t("companies.update") : t("companies.save")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {companies.length === 0 ? (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
            <p className="text-[var(--studio-text-muted)]">{t("companies.empty")}</p>
            <p className="text-sm text-[var(--studio-text-muted)] mt-1">{t("companies.empty_hint")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {companies.map((c) => (
            <Card
              key={c.id}
              className="border-[var(--studio-border)] shadow-none hover:shadow-sm transition-shadow"
              data-testid={`company-card-${c.id}`}
            >
              <CardContent className="p-5 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center text-[#2563EB] font-bold text-sm font-heading">
                      {c.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold font-heading">{c.name}</h3>
                      <p className="text-sm text-[var(--studio-text-muted)]">{c.industry}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {c.platforms?.map((p) => (
                      <Badge key={p} variant="secondary" className="text-xs capitalize">{p}</Badge>
                    ))}
                    <Badge variant="outline" className="text-xs">{c.posts_per_week ?? 0} Posts/Wo</Badge>
                    <Badge variant="outline" className="text-xs">{c.reels_per_week ?? 0} Reels/Wo</Badge>
                    {(c.stories_per_week ?? 0) > 0 && (
                      <Badge variant="outline" className="text-xs">{c.stories_per_week} Stories/Wo</Badge>
                    )}
                    {c.auto_approve && (
                      <Badge className="bg-green-600 text-white text-xs">Auto-Freigabe</Badge>
                    )}
                  </div>
                  {c.tonality && (
                    <p className="text-xs text-[var(--studio-text-muted)] mt-2">Ton: {c.tonality}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVoiceFor(c)}
                    data-testid={`voice-company-${c.id}`}
                    className="text-xs"
                    title="Brand Voice trainieren"
                  >
                    <Wand2 className="w-4 h-4 mr-1 text-[#2563EB]" /> Voice
                    {c.voice_profile && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRssFor(c)}
                    data-testid={`rss-company-${c.id}`}
                    className="text-xs"
                    title="RSS-Auto-Import"
                  >
                    <Rss className="w-4 h-4 mr-1 text-[#2563EB]" /> RSS
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAutoPostFor(c)}
                    data-testid={`autopost-company-${c.id}`}
                    className="text-xs"
                    title="KI: Aus Medien Posts erstellen"
                  >
                    <Images className="w-4 h-4 mr-1 text-[#2563EB]" /> Auto-Post
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAutoReplyFor(c)}
                    data-testid={`autoreply-company-${c.id}`}
                    className="text-xs"
                    title="Auto-Reply-Regeln fuer DMs/Kommentare"
                  >
                    <MessageCircle className="w-4 h-4 mr-1 text-[#2563EB]" /> Auto-Reply
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUnitsFor(c)}
                    data-testid={`units-company-${c.id}`}
                    className="text-xs"
                    title="Standorte / Geschaeftsbereiche"
                  >
                    <MapPin className="w-4 h-4 mr-1 text-[#2563EB]" /> Standorte
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(c)} data-testid={`edit-company-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} data-testid={`delete-company-${c.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <BrandVoiceDialog
        companyId={voiceFor?.id}
        companyName={voiceFor?.name}
        open={!!voiceFor}
        onOpenChange={(v) => { if (!v) setVoiceFor(null); }}
      />
      <RssFeedsDialog
        companyId={rssFor?.id}
        companyName={rssFor?.name}
        open={!!rssFor}
        onOpenChange={(v) => { if (!v) setRssFor(null); }}
      />
      <AutoPostDialog
        company={autoPostFor}
        open={!!autoPostFor}
        onOpenChange={(v) => { if (!v) setAutoPostFor(null); }}
      />
      <AutoReplyDialog
        companyId={autoReplyFor?.id}
        companyName={autoReplyFor?.name}
        open={!!autoReplyFor}
        onOpenChange={(v) => { if (!v) setAutoReplyFor(null); }}
      />
      <BusinessUnitsDialog
        companyId={unitsFor?.id}
        companyName={unitsFor?.name}
        open={!!unitsFor}
        onOpenChange={(v) => { if (!v) setUnitsFor(null); }}
      />
    </div>
  );
}

function AutoPostDialog({ company, open, onOpenChange }) {
  const [stats, setStats] = useState({ unused: 0, total: 0 });
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !company) { setResult(null); return; }
    setLoading(true);
    axios.get(`${API}/media?company_id=${company.id}&include_usage=true`, { withCredentials: true })
      .then((r) => {
        const all = r.data || [];
        const pool = company.auto_post_media_pool || "all";
        const eligible = pool === "uploaded" ? all.filter((m) => !m.is_ai_generated) : all;
        const unused = eligible.filter((m) => (m.used_in_count || 0) === 0);
        setStats({ unused: unused.length, total: eligible.length, pool });
      })
      .catch(() => setStats({ unused: 0, total: 0 }))
      .finally(() => setLoading(false));
  }, [open, company]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/companies/${company.id}/auto-generate-from-media`,
        { limit },
        { withCredentials: true },
      );
      setResult(r.data);
      toast.success(r.data?.message || "Fertig");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-Post fehlgeschlagen");
    }
    setGenerating(false);
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="autopost-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2563EB]" /> KI-Auto-Post fuer {company.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="border-[var(--studio-border)] shadow-none bg-[var(--studio-bg)]/40">
            <CardContent className="p-3 text-xs space-y-1">
              <p>Pool: <b>{stats.pool === "uploaded" ? "Nur eigene Uploads" : "Alle Medien (inkl. KI)"}</b></p>
              <p>{loading ? "Lade..." : `${stats.unused} unverwendete Medien (von ${stats.total} im Pool)`}</p>
            </CardContent>
          </Card>

          {!result && (
            <>
              <div>
                <Label className="text-xs">Maximal pro Lauf</Label>
                <Input
                  type="number"
                  min={1}
                  max={Math.max(1, stats.unused) || 30}
                  value={limit}
                  onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 1))}
                  className="mt-1"
                  data-testid="autopost-limit"
                />
                <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                  Empfehlung: 3-7 pro Lauf. Starts ab morgen, ein Post pro Tag.
                </p>
              </div>
              <Button
                onClick={generate}
                disabled={generating || stats.unused === 0}
                className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                data-testid="autopost-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {stats.unused === 0 ? "Keine unverwendeten Medien" : `Posts aus ${Math.min(limit, stats.unused)} Medien erzeugen`}
              </Button>
            </>
          )}

          {result && (
            <div className="space-y-2 text-xs">
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900">
                <p className="font-medium">{result.message}</p>
                <p className="text-[11px] mt-1">Drafts findest du in der Freigabe-Queue.</p>
              </div>
              {result.created?.length > 0 && (
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {result.created.map((c, i) => (
                    <div key={c.content_id} className="p-2 border border-[var(--studio-border)] rounded-md">
                      <p className="font-medium">{i + 1}. {c.format_type} · {c.schedule_date}</p>
                      <p className="text-[var(--studio-text-muted)] line-clamp-2">{c.caption_preview}</p>
                    </div>
                  ))}
                </div>
              )}
              {result.skipped?.length > 0 && (
                <p className="text-amber-600">{result.skipped.length} uebersprungen (KI-Fehler)</p>
              )}
              <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>Schliessen</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
